import React from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  TrophyIcon, 
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const achievements = [
    { icon: TrophyIcon, number: '75+', label: 'Major Installations' },
    { icon: StarIcon, number: '99.9%', label: 'System Uptime' },
    { icon: StarIcon, number: '5,000+', label: 'Concurrent Users' },
    { icon: ClockIcon, number: '13+', label: 'Years Experience' },
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="about" className="section-padding bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              About <span className="gradient-text">Dhanish Vijayan</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Visionary Chief Technology Officer with over 13 years of progressive experience in embedded systems, 
              enterprise software architecture, and innovative technology solutions serving thousands of users globally.
            </p>
          </motion.div>

          {/* Main Content Section */}
          <div className="max-w-5xl mx-auto mb-16">
            {/* About Content */}
            <motion.div variants={itemVariants} className="space-y-8">
              {/* Introduction Paragraphs */}
              <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-300">
                <p className="text-xl leading-relaxed mb-6">
                  As Chief Technology Officer at <strong className="text-primary-600 dark:text-primary-400">Elementz Engineers Guild Pvt Ltd</strong>, 
                  I have successfully architected and delivered transformative technology solutions that serve thousands of concurrent users, 
                  optimize energy consumption by 35-45%, and achieve industry-leading performance metrics.
                </p>
                
                <p className="text-lg leading-relaxed mb-6">
                  With over 13 years of progressive experience spanning embedded systems, enterprise streaming solutions, 
                  building automation systems, and specialized industrial applications, I have led the development of platforms 
                  serving 50+ institutional installations, supporting 5,000+ concurrent sessions, and processing critical real-time data.
                </p>
                
                <p className="text-lg leading-relaxed">
                  My leadership has driven the successful deployment of 6 major platform deployments with zero critical failures, 
                  while directing cross-functional teams of 12+ engineers across multiple specialized domains including IoT platforms, 
                  radiation detection systems, and AI-powered optimization solutions.
                </p>
              </div>

              {/* Info Cards Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Company Info */}
                <motion.div
                  className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 p-6 rounded-xl border border-primary-100 dark:border-primary-800"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center mb-3">
                    <BuildingOfficeIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Elementz Engineers Guild Pvt Ltd
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    Leading cross-functional teams of 12+ specialized engineers across multiple domains delivering platforms 
                    that achieve 35-45% energy savings, support 5,000+ concurrent users, and maintain 99.9% uptime with zero critical failures.
                  </p>
                </motion.div>

                {/* Mission Statement */}
                <motion.div
                  className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Mission & Vision
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Transforming complex technical challenges into innovative, scalable solutions that drive business success and industry advancement. 
                    Setting new standards for efficiency, reliability, and intelligent system management across diverse sectors.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Achievement Cards */}
          <motion.div variants={itemVariants} className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className="card p-6 text-center"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <achievement.icon className="h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    {achievement.number}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {achievement.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Core Values */}
          <motion.div variants={itemVariants} className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
              Core Values
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Technical Excellence', desc: 'Industry-leading performance with 99.9% uptime and zero critical failures' },
                { title: 'Innovation Leadership', desc: 'Pioneering AI-powered optimization and predictive analytics integration' },
                { title: 'Scalable Solutions', desc: 'Platforms serving 5,000+ concurrent users with enterprise-grade reliability' },
                { title: 'Business Impact', desc: 'Delivering measurable results: 35-45% efficiency improvements and cost savings' },
              ].map((value, index) => (
                <motion.div
                  key={index}
                  className="card p-6 border-l-4 border-primary-500"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {value.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            variants={itemVariants}
            className="text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-4">
              Ready to Transform Your Ideas into Reality?
            </h3>
            <p className="text-lg mb-6 text-primary-100">
              Let's discuss how I can help accelerate your technology initiatives and drive measurable business results.
            </p>
            <motion.button
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Start Your Project Today
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;